import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  trackButtonClick,
  trackElementHover,
  trackFormSubmit,
  trackInputFocus,
  trackLinkClick,
} from "@/utils/analytics/clickTracking";
import { canTrackMixpanel } from "@/utils/analytics/consent";
import { trackProductEvent } from "@/utils/analytics";

const getInteractiveTarget = (target, selector) =>
  target?.closest ? target.closest(selector) : null;

export const useClickTracking = (user) => {
  const location = useLocation();
  const pageStartRef = useRef(Date.now());
  const hoverTimeoutRef = useRef(null);
  const activeFormRef = useRef(null);
  const formSubmittedRef = useRef(false);

  // Track time on page when route changes
  useEffect(() => {
    if (!canTrackMixpanel(user)) return;
    const durationSeconds = Math.max(
      0,
      Math.floor((Date.now() - pageStartRef.current) / 1000)
    );
    if (activeFormRef.current && !formSubmittedRef.current) {
      trackProductEvent(
        "form_abandon",
        {
          form_id: activeFormRef.current,
          page: location.pathname,
          role: user?.role,
        },
        user
      );
    }
    if (durationSeconds > 0) {
      trackProductEvent(
        "time_on_page",
        {
          page: location.pathname,
          duration_seconds: durationSeconds,
          role: user?.role,
        },
        user
      );
    }
    activeFormRef.current = null;
    formSubmittedRef.current = false;
    pageStartRef.current = Date.now();
  }, [location.pathname, user]);

  useEffect(() => {
    if (!canTrackMixpanel(user)) return;

    const trackFirstAction = (actionType) => {
      try {
        if (sessionStorage.getItem("__analytics_first_action")) return;
        const raw = sessionStorage.getItem("__analytics_page_stats");
        const stats = raw ? JSON.parse(raw) : {};
        const startTs = stats.session_start_ts || Date.now();
        const durationMs = Math.max(0, Date.now() - startTs);
        trackProductEvent(
          "session_first_action",
          {
            page: window.location.pathname,
            duration_ms: durationMs,
            action_type: actionType,
            role: user?.role,
          },
          user
        );
        sessionStorage.setItem("__analytics_first_action", "true");
      } catch {
        // Ignore storage issues
      }
    };

    const handleClick = (event) => {
      const target = event.target;
      const page = window.location.pathname;
      const button = getInteractiveTarget(
        target,
        "button,[role='button'],input[type='button'],input[type='submit']"
      );
      if (button) {
        trackFirstAction("click");
        trackButtonClick(button, page, user);
        return;
      }

      const link = getInteractiveTarget(target, "a[href]");
      if (link) {
        trackFirstAction("click");
        trackLinkClick(link, page, user);
        return;
      }

      const form = getInteractiveTarget(target, "form");
      if (form) {
        trackFormSubmit(form, page, user);
        activeFormRef.current = null;
        formSubmittedRef.current = true;
      }
    };

    const handleFocusIn = (event) => {
      const target = event.target;
      const page = window.location.pathname;
      const input = getInteractiveTarget(
        target,
        "input,textarea,select"
      );
      if (input) {
        trackFirstAction("focus");
        trackInputFocus(input, page, user);
        const form = getInteractiveTarget(input, "form");
        if (form) {
          activeFormRef.current =
            form.getAttribute("data-analytics-id") ||
            form.getAttribute("data-testid") ||
            form.id ||
            "unknown";
          formSubmittedRef.current = false;
        }
      }
    };

    const handleMouseOver = (event) => {
      clearTimeout(hoverTimeoutRef.current);
      const target = event.target;
      const interactive = getInteractiveTarget(
        target,
        "button,[role='button'],a[href]"
      );
      if (!interactive) return;

      hoverTimeoutRef.current = setTimeout(() => {
        const page = window.location.pathname;
        trackElementHover(interactive, page, user);
      }, 400);
    };

    window.addEventListener("click", handleClick, true);
    window.addEventListener("focusin", handleFocusIn, true);
    window.addEventListener("mouseover", handleMouseOver, true);

    return () => {
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("focusin", handleFocusIn, true);
      window.removeEventListener("mouseover", handleMouseOver, true);
      clearTimeout(hoverTimeoutRef.current);
    };
  }, [user]);
};
